import React, { useRef } from 'react';
import useRequest from '../../../hooks/use-request.hook';
import { useTranslation } from 'react-i18next';
import useModal from '../../../hooks/use-close-modal.hook';
import Icon from '../../../components/forms/icon.component';
import LoaderModalTable from '../../../components/loader/loader-table-modal.component';
import Title from '../../../components/forms/titles.component';
interface PermissionsViewProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  payload: any;
}
interface Permission {
  read?: boolean;
  write?: boolean;
}

interface Permissions {
  [key: string]: Permission;
}

function PermissionsView({ show, onRequestClose, payload }: PermissionsViewProps) {
  const { t } = useTranslation();
  const { isRequesting } = useRequest();
  const modalRef = useRef<HTMLDivElement>(null);
  useModal(modalRef, show);
  const permissions: Permissions = payload?.permissions || {};
  const permissionTranslations: Record<string, string> = {
    accounts: t('asaid.link-accounts'),
    reports: t('asaid.link-title-reports'),
    roles: t('asaid.link-roles'),
    users: t('asaid.link-users'),
    blacklist: t('asaid.link-blacklist'),
    campaigns: t('campaigns.send-campaigns'),
    templates: t('asaid.link-templates'),
    dashboard: t('asaid.link-dashboard'),
    channels: t('asaid.link-menu-channels')
  };

  const moduleIcons: Record<string, string> = {
    accounts: 'ki-duotone ki-user ',
    reports: 'ki-duotone ki-document',
    roles: 'ki-duotone ki-colors-square',
    users: 'ki-duotone ki-profile-user',
    blacklist: 'ki-duotone ki-shield',
    campaigns: 'ki-duotone ki-send',
    templates: 'ki-duotone ki-picture',
    dashboard: 'ki-duotone ki-dashboard',
    channels: 'ki-duotone ki-technology-4'
  };

  const closeModal = () => {
    onRequestClose(false);
  };

  return (
    <div className="modal fade" ref={modalRef} data-bs-backdrop="static">
      <div className="modal-dialog mw-1000px">
        <div className="modal-content">
          <div className="modal-header justify-content-end border-0 pb-0">
            <div
              className="btn btn-sm btn-icon btn-active-light-primary"
              onClick={() => closeModal()}
            >
              <Icon icon="ki-cross" size="fs-2x" />
            </div>
          </div>
          <>
            <div className="modal-body pt-0 pb-15 px-5 px-xl-20">
              <div className="mt-1 text-center">
                <h1 className="mb-3">
                  {t('roles.permissions-title')} <br />
                  <a className="link-primary fw-bold ms-2">{payload?.name}</a>
                </h1>
              </div>
              <div className="separator border-2 my-5"></div>
              <div className="d-flex justify-content-center row mb-10">
                <div className="col-lg-12 mb-10 mb-lg-0 h-100 p-10">
                  <div className="row rounded h-100 p-10">
                    {!isRequesting ? (
                      <>
                        <Title
                          type={'info'}
                          title={t('roles.permissions-title')}
                          icon="ki-gear"
                          subtitle={t('roles.permissions-subtitle')}
                        />
                        <div className="overflow-scroll">
                          <div className="table-responsive">
                            <table className="table table-thead-bordered table-nowrap table-align-middle table-first-col-px-0">
                              <thead className="thead-light">
                                <tr>
                                  <th>
                                    <strong>{t('roles.module')}</strong>
                                  </th>
                                  <th className="text-center">
                                    <Icon icon="ki-eye" size="fs-2" />
                                    <p className="m-0">
                                      <strong>{t('roles.read')}</strong>
                                    </p>
                                  </th>
                                  <th className="text-center">
                                    <Icon icon="ki-plus-square" size="fs-2" />
                                    <p className="m-0">
                                      <strong>{t('roles.write')}</strong>
                                    </p>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(permissions).map(
                                  ([module, values]: [string, Permission]) => (
                                    <tr key={module}>
                                      <td>
                                        <div className="d-flex justify-content-between align-items-center w-100">
                                          <div className="d-flex align-items-center">
                                            <span className="menu-icon">
                                              <Icon
                                                icon={`${moduleIcons[module] || 'ki-duotone ki-settings'}`}
                                                size="fs-2x"
                                                type={`${values.read ? 'primary' : ''}`}
                                              />
                                            </span>
                                            <span className="ms-2 text-nowrap">
                                              {permissionTranslations[module] || module}
                                            </span>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="align-middle text-center">
                                        <div className="form-check form-check-inline m-0">
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={values.read || false}
                                            disabled
                                          />
                                        </div>
                                      </td>
                                      <td className="align-middle text-center">
                                        <div className="form-check form-check-inline m-0">
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={values.write || false}
                                            disabled
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      <LoaderModalTable />
                    )}
                  </div>
                </div>
              </div>
              <div className="d-flex flex-center flex-row-fluid">
                <button
                  type="button"
                  className="btn btn-light hover-scale"
                  onClick={() => closeModal()}
                >
                  {t('general.close')}
                </button>
              </div>
            </div>
          </>
        </div>
      </div>
    </div>
  );
}

export default PermissionsView;
